// XenoarcComponent.jsx - Refactored to use reusable SystemSettings
import { useCallback } from "react";
import DropdownExchange from "../commons/DropdownExchange";
import DropdownPair from "../commons/DropdownPair";
import { BOT_STRATEGIES, useBot } from "../../context/botContext";
import XenoarcV2Setting from "./xenoarc/XenoarcV2Setting";
import XenoarcGridSetting from "./xenoarc/XenoarcGridSetting";
import XenoarcV3Setting from "./xenoarc/XenoarcV3Setting";
import AvailableForTrade from "./common/AvailableForTrade";
import { useXenoarcState } from "../../hooks/useXenoarcState";
import SystemSettings, {
  LoadingState,
  EmptyState,
  SettingsSection,
} from "./SystemSettings";
import InputWithIcon from "../commons/InputWithIcon";
import CustomSlider from "../commons/CustomSlider";
import { useBotProfileQuery } from "../../hooks/useBotQueries";

// Helper Functions
export const getBotCapital = (strategy, formXenoV2, formXenoGrid) => {
  if (strategy === BOT_STRATEGIES.GRID) return formXenoGrid?.settings?.amount;
  if (strategy === BOT_STRATEGIES.XENO_ARC)
    return formXenoV2?.settings?.capital;
  return 0;
};

export const getBotTradeSize = (strategy, formXenoV2, formXenoGrid) => {
  if (strategy === BOT_STRATEGIES.GRID)
    return formXenoGrid?.settings?.trade_size;
  if (strategy === BOT_STRATEGIES.XENO_ARC) return formXenoV2?.settings?.amount;
  return 0;
};

const getExchangeName = (strategy, formXenoV2, formXenoGrid) => {
  if (strategy === BOT_STRATEGIES.GRID) return formXenoGrid?.exchange_name;
  if (strategy === BOT_STRATEGIES.XENO_ARC) return formXenoV2?.exchange_name;
  return null;
};

const getCoinName = (strategy, formXenoV2, formXenoGrid) => {
  if (strategy === BOT_STRATEGIES.GRID) return formXenoGrid?.coin_name;
  if (strategy === BOT_STRATEGIES.XENO_ARC) return formXenoV2?.coin_name;
  return null;
};

const XenoarcComponent = ({
  pairs,
  exchanges,
  setSelectedExchange,
  selectedExchange,
  selectedPair,
  setSelectedPair,
  selectedBot,
  formXenoV2,
  setFormXenoV2,
  setFormXenoGrid,
  formXenoGrid,
  userBalance,
  loadingBalance,
  loadingPair,
  loadingExchanges,
  errors,
  setErrors,
  actualUserBalance,
  initialBotTradeSize,
  additionalTradeSize,
  setAdditionalTradeSize,
  botDetail,
  additionalCapital,
  setAdditionalCapital,
}) => {
  const { openDialogBot, selectedSetting, setSelectedSetting } = useBot();

  const { data: systemResults, isLoading: loadingGetSetting } =
    useBotProfileQuery(selectedBot?.id, selectedPair?.id);

  const {
    strategy,
    baseCapital,
    activeIndex,
    setActiveIndex,
    capitalInput,
    percentage,
    handleCapitalChange,
    handleCapitalBlur,
    handleSliderChange,
    handleChangeTradeSize,
    setSystemSettingData,
  } = useXenoarcState({
    userBalance,
    selectedBot,
    formXenoV2,
    setFormXenoV2,
    formXenoGrid,
    setFormXenoGrid,
    setSelectedExchange,
    selectedExchange,
    setSelectedPair,
    selectedPair,
    openDialogBot,
    selectedSetting,
    setSelectedSetting,
    setErrors,
    errors,
    actualUserBalance,
    additionalCapital,
    setAdditionalCapital,
  });

  let capitalError;
  if (strategy === BOT_STRATEGIES.GRID) {
    capitalError = errors?.amount;
  } else if (strategy === BOT_STRATEGIES.XENO_ARC) {
    capitalError = errors?.capital;
  }

  const handleSystemSettingClick = useCallback(
    (setting) => {
      setActiveIndex(setting.key);
      let formData;

      if (strategy === BOT_STRATEGIES.GRID)
        formData = { ...(formXenoGrid || {}) };
      if (strategy === BOT_STRATEGIES.XENO_ARC)
        formData = { ...(formXenoV2 || {}) };

      const profiles = systemResults?.[setting?.key];
      if (!formData || !Array.isArray(profiles) || profiles.length === 0)
        return;

      const profile = profiles[0];
      const params = profile?.parameters || [];

      const updatedSettings = { ...(formData.settings || {}) };

      params.forEach(({ parameter_key, parameter_value }) => {
        updatedSettings[parameter_key] = parameter_value;
      });

      // Store original system data
      let originDataSystem = { settings: { ...updatedSettings } };
      setSystemSettingData(originDataSystem);

      // Maintain capital / amount values correctly per strategy
      if (strategy === BOT_STRATEGIES.GRID) {
        updatedSettings.amount = formData?.settings?.amount;
      } else if (strategy === BOT_STRATEGIES.XENO_ARC) {
        updatedSettings.capital = formData?.settings?.capital;
      }

      const updatedForm = { ...formData, settings: updatedSettings };

      if (strategy === BOT_STRATEGIES.GRID) setFormXenoGrid(updatedForm);
      if (strategy === BOT_STRATEGIES.XENO_ARC) setFormXenoV2(updatedForm);
    },
    [
      strategy,
      formXenoGrid,
      formXenoV2,
      systemResults,
      setActiveIndex,
      setSystemSettingData,
      setFormXenoGrid,
      setFormXenoV2,
    ],
  );

  const currentExchangeName = getExchangeName(
    strategy,
    formXenoV2,
    formXenoGrid,
  );
  const currentCoinName = getCoinName(strategy, formXenoV2, formXenoGrid);

  const handleDropdownChange = (setter) => (val) => {
    setter(val);
    if (setter === setSelectedExchange) {
      setSelectedPair(null);
    }
  };

  // isEdit
  const isEdit = openDialogBot === "step-bot-2-edit";

  const initCapitalValue = isEdit
    ? strategy === BOT_STRATEGIES.GRID
      ? Number(botDetail?.setting?.amount) || 0
      : strategy === BOT_STRATEGIES.XENO_ARC
        ? Number(botDetail?.setting?.capital) || 0
        : 0
    : 0;

  // Determine which keys to exclude from system settings display
  const excludeKeys =
    strategy === BOT_STRATEGIES.GRID ? ["amount"] : ["capital"];

  return (
    <>
      <DropdownExchange
        id="exchange"
        value={selectedExchange}
        onChange={handleDropdownChange(setSelectedExchange)}
        options={exchanges}
        disabled={!!currentExchangeName}
        placeholder={currentExchangeName || "Select Exchange"}
        loading={loadingExchanges}
      />
      <DropdownPair
        id="pair"
        value={selectedPair}
        onChange={handleDropdownChange(setSelectedPair)}
        options={pairs}
        disabled={!!currentCoinName}
        placeholder={currentCoinName || "Select Pair"}
        loading={loadingPair}
      />

      <div>
        <AvailableForTrade
          userBalance={actualUserBalance}
          loadingBalance={loadingBalance}
          selectedExchange={selectedExchange}
          selectedPair={selectedPair}
          exchangeName={currentExchangeName}
          coinName={currentCoinName}
        />
      </div>

      <div className="space-y-2 !mb-5">
        <span className="text-[#697077] text-sm">
          {isEdit ? (
            <>
              Add Capital allocation{" "}
              <span class="text-gray-400 ">(current: {initCapitalValue})</span>
            </>
          ) : (
            "Capital allocation"
          )}
        </span>
        <InputWithIcon
          value={capitalInput}
          onChange={handleCapitalChange}
          placeholder="Capital Allocation"
          rightIcon={selectedPair?.base_currency || "USDT"}
          inputClassName="text-right"
        />
        <div className="!mt-4">
          <CustomSlider
            percentage={percentage}
            setPercentage={handleSliderChange}
          />
        </div>
        {capitalError && (
          <p className="text-red-500 text-sm mt-1.5">{capitalError}</p>
        )}
      </div>

      <div className="border border-gray-300 mt-5 rounded-[10px] cursor-pointer relative divide-y">
        <div className="bg-[#F2F4F8] p-3">
          <span className="text-black block">Settings</span>
          <span className="block text-[#697077] capitalize">
            Setting Your {strategy} Bot
          </span>
        </div>

        {!isEdit && (
          <SettingsSection
            title="By System"
            settingKey="system"
            selectedSetting={selectedSetting}
            setSelectedSetting={setSelectedSetting}
          >
            {loadingGetSetting ? (
              <LoadingState message="Loading system data..." />
            ) : !systemResults || Object.keys(systemResults).length === 0 ? (
              <EmptyState onLinkClick={() => setSelectedSetting("custom")} />
            ) : (
              <SystemSettings
                strategy={strategy}
                settings={systemResults}
                activeIndex={activeIndex}
                onSettingClick={handleSystemSettingClick}
                excludeKeys={excludeKeys}
              />
            )}
          </SettingsSection>
        )}

        <SettingsSection
          title="Customize Own"
          settingKey="custom"
          selectedSetting={selectedSetting}
          setSelectedSetting={setSelectedSetting}
        >
          <div className="rounded-[10px] cursor-pointer relative space-y-5">
            {strategy === BOT_STRATEGIES.GRID ? (
              <XenoarcGridSetting
                formXenoGrid={formXenoGrid}
                setFormXenoGrid={setFormXenoGrid}
                userBalance={userBalance}
                selectedPair={selectedPair}
                baseCapital={baseCapital}
                errors={errors}
                initialBotTradeSize={initialBotTradeSize}
                additionalTradeSize={additionalTradeSize}
                setAdditionalTradeSize={setAdditionalTradeSize}
              />
            ) : strategy === BOT_STRATEGIES.XENO_ARC ? (
              <XenoarcV2Setting
                setFormXenoV2={setFormXenoV2}
                formXenoV2={formXenoV2}
                userBalance={userBalance}
                selectedPair={selectedPair}
                baseCapital={baseCapital}
                errors={errors}
                initialBotTradeSize={initialBotTradeSize}
                additionalTradeSize={additionalTradeSize}
                setAdditionalTradeSize={setAdditionalTradeSize}
              />
            ) : (
              <XenoarcV3Setting />
            )}
          </div>
        </SettingsSection>
      </div>
    </>
  );
};

export default XenoarcComponent;
